import React, { useState, useEffect, useRef } from 'react';
import { userRequest } from '../utils/requestMethods';
import { Header, ButtonLoading, BackButton } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { ImSpinner } from "react-icons/im";
import { FiCheckCircle, FiAlertCircle, FiChevronDown, FiSearch } from 'react-icons/fi';

const InfoRow = ({ label, value }) => (
    <div className="flex flex-col gap-0.5 py-3 border-b border-gray-100 last:border-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{value ?? '—'}</p>
    </div>
);

const BankSelect = ({ banks, value, onChange, disabled }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const selected = banks.find(b => b.bank_code === value);
    const filtered = banks.filter(b =>
        b.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);

    const handleSelect = (bank) => {
        onChange(bank);
        setOpen(false);
        setSearch('');
    };

    return (
        <div ref={containerRef} className="relative mt-1">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between w-full rounded-md bg-gray-100 border-transparent px-3 py-2 text-sm text-gray-900 dark:text-gray-100 dark:bg-gray-700 focus:outline-none focus:bg-white focus:border-gray-500 disabled:opacity-50"
            >
                <span className={selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
                    {selected ? selected.name : 'Select bank'}
                </span>
                <FiChevronDown className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <FiSearch className="text-gray-400 shrink-0 text-sm" />
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search banks..."
                            className="flex-1 text-sm bg-transparent border-none outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400"
                        />
                    </div>
                    <ul className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-gray-400 text-center">No banks found</li>
                        ) : filtered.map(bank => (
                            <li
                                key={bank.bank_code}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(bank);
                                }}
                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-indigo-50 dark:hover:bg-gray-700 ${
                                    bank.bank_code === value ? 'bg-indigo-50 text-indigo-600 font-medium dark:bg-gray-700' : 'text-gray-700 dark:text-gray-200'
                                }`}
                            >
                                {bank.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

const PaymentCompliance = ({ history }) => {
    const { user, school } = useStateContext();

    const [banks, setBanks] = useState([]);
    const [bankDetails, setBankDetails] = useState(null);
    const [bankCode, setBankCode] = useState("");
    const [bankName, setBankName] = useState("");
    const [accountName, setAccountName] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resolving, setResolving] = useState(false);

    useEffect(() => {
        if (school?.counter_party) {
            setBankDetails(school.counter_party);
        }
    }, [school]);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const { data } = await userRequest.get(`/banking/withdrawal-banks`);
                const result = data?.bank_codes || [];
                const seen = new Set();
                const unique = result.filter(b => {
                    if (seen.has(b.bank_code)) return false;
                    seen.add(b.bank_code);
                    return true;
                });
                setBanks(unique);
            } catch (error) {
                console.log(error);
            }
        };
        fetchBanks();
    }, [user]);

    const resolveAccount = async (code, number) => {
        if (!code || !number) return;
        setResolving(true);
        setAccountName("");
        try {
            const { data } = await userRequest.post(`banking/validate-account`, {
                accounts: [{ bankCode: code, accountNumber: number }]
            });
            const result = data?.result?.[0];
            setAccountName(result?.beneficiaryName || "");
            if (!result?.beneficiaryName) {
                setError("Could not resolve account name.");
                setTimeout(() => setError(""), 5000);
            }
        } catch (error) {
            console.log(error);
            setError("Error, unable to resolve account!");
            setTimeout(() => setError(""), 5000);
        } finally {
            setResolving(false);
        }
    };

    const resolveHandler = async (bank) => {
        setBankCode(bank.bank_code);
        setBankName(bank.name);
        await resolveAccount(bank.bank_code, accountNumber);
    };

    const handleAccountNumberBlur = async () => {
        await resolveAccount(bankCode, accountNumber);
    };

    const saveHandler = async (e) => {
        e.preventDefault();
        if (!accountName || !accountNumber || !bankCode || !bankName) {
            setError("Please fill all the fields");
            setTimeout(() => setError(""), 5000);
            return;
        }

        setLoading(true);
        try {
            await userRequest.post(`banking/wallets/${school?._id}/withdrawal-account`, {
                bankCode,
                accountNumber,
                accountName,
                bankName,
            });
            setSuccess("Account information saved successfully");
            setBankDetails({ bank_name: bankName, account_name: accountName, account_number: accountNumber });
            setTimeout(() => setSuccess(""), 3000);
        } catch (error) {
            console.log(error);
            setError(error?.response?.data?.message || "Failed to save account details");
            setTimeout(() => setError(""), 5000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="m-2 md:m-10 mt-24 shadow p-2 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl min-h-screen">
            <BackButton history={history} />
            <Header category="Onboarding" title="Payment Information" />
            <div className="mt-2 max-w-full divide-y">
                <div className="py-2">
                    <h3 className="text-1xl font-bold dark:text-gray-200">Withdrawal Bank Account</h3>
                    <hr />
                </div>
            </div>

            {bankDetails?.account_name ? (
                <div className="py-4 max-w-sm">
                    <div className="flex items-center gap-2 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 mb-4">
                        <FiCheckCircle className="text-green-500 shrink-0" />
                        <p className="text-sm font-medium text-green-700">Withdrawal account is set up</p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 px-5 py-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-3 mb-1">Bank Details</p>
                        <InfoRow label="Bank Name" value={bankDetails?.bank_name} />
                        <InfoRow label="Account Name" value={bankDetails?.account_name} />
                        <InfoRow label="Account Number" value={bankDetails?.account_number} />
                    </div>
                </div>
            ) : (
                <div className="py-4 max-w-sm">
                    {success && (
                        <div className="flex items-center gap-2 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 mb-4">
                            <FiCheckCircle className="text-green-500 shrink-0" />
                            <p className="text-sm font-medium text-green-700">{success}</p>
                        </div>
                    )}
                    {error && (
                        <div className="flex items-center gap-2 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 mb-4">
                            <FiAlertCircle className="text-red-500 shrink-0" />
                            <p className="text-sm font-medium text-red-700">{error}</p>
                        </div>
                    )}
                    <div className="rounded-2xl border border-gray-200 px-5 py-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Bank Details</p>
                        <form onSubmit={saveHandler}>
                            <div className="grid grid-cols-1 gap-6">
                                <label className="block">
                                    <span className="text-gray-700 dark:text-gray-200">Bank Account Number</span>
                                    <input
                                        type="text"
                                        value={accountNumber}
                                        onChange={(e) => setAccountNumber(e.target.value)}
                                        onBlur={handleAccountNumberBlur}
                                        placeholder="Enter 10-digit account number"
                                        className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-gray-700 dark:text-gray-200">Bank Name</span>
                                    <BankSelect
                                        banks={banks}
                                        value={bankCode}
                                        onChange={resolveHandler}
                                        disabled={banks.length === 0}
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-gray-700 dark:text-gray-200">
                                        Account Name
                                        {resolving && (
                                            <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-500 font-normal">
                                                <ImSpinner className="animate-spin" /> Verifying...
                                            </span>
                                        )}
                                    </span>
                                    <input
                                        type="text"
                                        value={accountName}
                                        readOnly
                                        placeholder="Auto-filled after verification"
                                        className="mt-1 block w-full rounded-md bg-gray-100 border-transparent text-gray-500 cursor-not-allowed"
                                    />
                                </label>
                                <div className="text-right">
                                    {loading ? (
                                        <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-400" />
                                    ) : (
                                        <button
                                            type="submit"
                                            disabled={resolving || !accountName}
                                            className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Save Details
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <hr />
        </div>
    );
};

export default PaymentCompliance;
